import type { SceneThresholds } from "./scene/responsive";
import type { ScrollMeasurement } from "./scene/scrollLayout";
import { computeSectionProgress } from "./scene/utils";

/** Offset, при котором верх секции About достигает верха вьюпорта.
 *  drei <Scroll html> сдвигает оверлей 1:1 со scrollTop, а прокручиваемая
 *  высота = contentHeight − vh, поэтому порог = anchorTop / (contentHeight − vh).
 *  null, пока якорь не измерен (первые кадры / fallback-раскладка). */
export function aboutStartOffset(m: ScrollMeasurement | null): number | null {
  if (!m) return null;
  const top = m.anchors.about;
  const span = m.contentHeight - m.vh;
  if (!Number.isFinite(top) || span <= 0) return null;
  return Math.min(1, top / span);
}

export type MainNavState = { activeId: string; progress: number };

/** Активный пункт main-навигации и заполнение его прогресс-бара.
 *  Всё в offset-пространстве (offset = t / tScale): для home/services/decks
 *  это эквивалентно t-порогам (там t < tMax), а хвост About живёт за точкой
 *  парковки камеры, где t насыщен и непригоден. */
export function mainNavState(args: {
  offset: number;
  activeScene: string;
  thresholds: SceneThresholds;
  tScale: number;
  tMax: number;
  aboutStart: number | null;
}): MainNavState {
  const { offset, activeScene, thresholds: th, tScale, tMax, aboutStart } = args;
  const o = (t: number) => t / tScale;

  if (aboutStart != null && offset >= aboutStart) {
    return { activeId: "about", progress: computeSectionProgress(offset, aboutStart, 1) };
  }
  if (activeScene === "home") {
    return { activeId: "home", progress: computeSectionProgress(offset, 0, o(th.homeServices)) };
  }
  if (activeScene === "services") {
    return {
      activeId: "services",
      progress: computeSectionProgress(offset, o(th.homeServices), o(th.servicesDecks)),
    };
  }
  if (activeScene === "decks") {
    return {
      activeId: "decks",
      progress: computeSectionProgress(offset, o(th.servicesDecks), o(th.decksReviews)),
    };
  }
  if (activeScene === "reviews") {
    return {
      activeId: "reviews",
      progress: computeSectionProgress(offset, o(th.decksReviews), aboutStart ?? o(tMax)),
    };
  }
  return { activeId: activeScene, progress: 0 };
}
